import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Gavel, AlertCircle, Loader, RefreshCw, User, Inbox, FileText, Bot, CheckSquare } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../contexts/AuthContext';
import { appealsApiService } from '../services/appealsApi';
import { AppealListItem, AppealStats, AppealFilters as AppealFiltersType, PaginatedAppealsResponse, AppealStatus } from '../types/appeals';
import { AppealStatsCards } from '../components/appeals/AppealStatsCards';
import { AppealFilters } from '../components/appeals/AppealFilters';
import { AppealCard } from '../components/appeals/AppealCard';
import { usernameCacheService } from '../services/usernameCache';
import { cn } from '../utils/cn';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

/** Layout-mirroring skeleton for the appeal card list. */
const ListSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <div className="space-y-3" aria-busy="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="glass-card p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <SkeletonLoader className="w-9 h-9 rounded-lg" />
            <div className="space-y-2">
              <SkeletonLoader className="h-4 w-40" />
              <SkeletonLoader className="h-3 w-28" />
            </div>
          </div>
          <SkeletonLoader className="h-5 w-20 rounded-full" />
        </div>
        <SkeletonLoader className="h-9 w-full" />
        <SkeletonLoader className="h-3 w-3/4" />
      </div>
    ))}
  </div>
);

type PhaseFilter = 'all' | 'staff_action' | 'bot_pipeline' | 'closed';

const STAFF_STATUSES: AppealStatus[] = ['pending', 'awaiting_staff'];
const BOT_STATUSES: AppealStatus[] = ['staff_answered', 'in_jury', 'jury_decided'];
const CLOSED_STATUSES: AppealStatus[] = ['approved', 'denied', 'escalated', 'cancelled'];

export function AppealsView() {
  useDocumentTitle('Ban Appeals');

  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const [searchParams] = useSearchParams();

  const initialUserUuid = searchParams.get('user_uuid') || '';
  const initialBanId = searchParams.get('ban_id') || '';
  const initialStatus = searchParams.get('status') || '';

  const [stats, setStats] = useState<AppealStats | null>(null);
  const [appeals, setAppeals] = useState<AppealListItem[]>([]);
  const [totalAppeals, setTotalAppeals] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'mine'>('all');
  const [phaseFilter, setPhaseFilter] = useState<PhaseFilter>('all');
  const [filters, setFilters] = useState<AppealFiltersType>({
    page: 1,
    limit: 50,
    user_uuid: initialUserUuid || undefined,
    ban_id: initialBanId ? parseInt(initialBanId, 10) : undefined,
    status: (initialStatus as AppealFiltersType['status']) || undefined,
  });

  const debouncedSearch = useDebounce(searchTerm, 300);

  const canManage = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Bans_Admin') || groups.includes('SPY_Bans_Superadmins') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const canViewAssigned = useMemo(() => {
    if (canManage) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Bans_Helper');
  }, [identity, canManage]);

  const loadStats = useCallback(async () => {
    setIsStatsLoading(true);
    try {
      const data = await appealsApiService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load appeal stats:', err);
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  const loadAppeals = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (activeTab === 'mine') {
        const data = await appealsApiService.getMyAppeals(filters.status || undefined);
        setAppeals(data);
        setTotalAppeals(data.length);
      } else {
        const data = await appealsApiService.listAppeals(filters);
        if ('results' in data) {
          setAppeals((data as PaginatedAppealsResponse).results);
          setTotalAppeals((data as PaginatedAppealsResponse).total);
        } else {
          setAppeals(data as AppealListItem[]);
          setTotalAppeals((data as AppealListItem[]).length);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load appeals');
    } finally {
      setIsLoading(false);
    }
  }, [filters, activeTab]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadAppeals();
  }, [loadAppeals]);

  useEffect(() => {
    if (appeals.length > 0) {
      const uuids = [
        ...appeals.map(a => a.user_uuid),
        ...appeals.map(a => a.assigned_staff_id).filter(Boolean),
      ].filter(Boolean) as string[];
      usernameCacheService.prefetchUsernames(uuids);
    }
  }, [appeals]);

  const filteredAppeals = useMemo(() => {
    let result = appeals;

    if (phaseFilter !== 'all') {
      const phaseStatuses =
        phaseFilter === 'staff_action' ? STAFF_STATUSES :
        phaseFilter === 'bot_pipeline' ? BOT_STATUSES :
        CLOSED_STATUSES;
      result = result.filter(a => phaseStatuses.includes(a.status));
    }

    if (debouncedSearch) {
      const search = debouncedSearch.toLowerCase();
      result = result.filter(a =>
        a.username?.toLowerCase().includes(search) ||
        a.user_uuid?.toLowerCase().includes(search) ||
        String(a.ban_id).includes(search) ||
        String(a.id).includes(search) ||
        a.ban_reason?.toLowerCase().includes(search)
      );
    }

    return result;
  }, [appeals, debouncedSearch, phaseFilter]);

  const phaseCounts = useMemo(() => {
    return {
      staff_action: appeals.filter(a => STAFF_STATUSES.includes(a.status)).length,
      bot_pipeline: appeals.filter(a => BOT_STATUSES.includes(a.status)).length,
      closed: appeals.filter(a => CLOSED_STATUSES.includes(a.status)).length,
    };
  }, [appeals]);

  const handleRefresh = () => {
    loadStats();
    loadAppeals();
  };

  const currentPage = filters.page || 1;
  const totalPages = Math.ceil(totalAppeals / (filters.limit || 50));

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Moderation"
        title="Ban Appeals"
        description="Manage ban appeal submissions, assign reviewers, and track jury decisions."
        icon={Gavel}
        meta={isLoading ? (
          <Loader className="w-3.5 h-3.5 animate-spin text-glass-400" aria-label="Loading appeals" />
        ) : !error ? (
          <span className="badge badge-neutral">{totalAppeals.toLocaleString()} total</span>
        ) : undefined}
        actions={
          <button onClick={handleRefresh} className="glass-button" disabled={isLoading}>
            <RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
            Refresh
          </button>
        }
      />

      <AppealStatsCards stats={stats} isLoading={isStatsLoading} />

      {/* Toolbar: primary tabs, phase quick-filters, search & filters */}
      <div className="glass-card p-3 space-y-3">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          {/* Primary tabs */}
          <div className="segmented" role="tablist" aria-label="Appeal queue">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'all'}
              data-active={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
              className="segmented-item"
            >
              <Inbox className="w-4 h-4" />
              All Appeals
            </button>
            {canViewAssigned && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'mine'}
                data-active={activeTab === 'mine'}
                onClick={() => setActiveTab('mine')}
                className="segmented-item"
              >
                <User className="w-4 h-4" />
                My Assigned
              </button>
            )}
          </div>

          {/* Phase quick-filters */}
          <div className="flex flex-wrap gap-1.5 md:ml-auto">
            <button
              type="button"
              onClick={() => setPhaseFilter('all')}
              aria-pressed={phaseFilter === 'all'}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                phaseFilter === 'all'
                  ? 'bg-accent-500/15 border-accent-500/40 text-accent-300'
                  : 'bg-glass-100/5 border-glass-200/10 text-glass-400 hover:text-white hover:border-glass-200/30'
              )}
            >
              All Phases
            </button>
            <button
              type="button"
              onClick={() => setPhaseFilter('staff_action')}
              aria-pressed={phaseFilter === 'staff_action'}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors flex items-center gap-1.5',
                phaseFilter === 'staff_action'
                  ? 'bg-orange-500/15 border-orange-500/40 text-orange-300'
                  : 'bg-glass-100/5 border-glass-200/10 text-glass-400 hover:text-white hover:border-glass-200/30'
              )}
            >
              <FileText className="w-3 h-3" />
              <span>Needs Staff Action</span>
              {phaseCounts.staff_action > 0 && (
                <span className="bg-orange-500/30 text-orange-300 px-1.5 rounded-full text-[10px] tabular-nums">{phaseCounts.staff_action}</span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setPhaseFilter('bot_pipeline')}
              aria-pressed={phaseFilter === 'bot_pipeline'}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors flex items-center gap-1.5',
                phaseFilter === 'bot_pipeline'
                  ? 'bg-purple-500/15 border-purple-500/40 text-purple-300'
                  : 'bg-glass-100/5 border-glass-200/10 text-glass-400 hover:text-white hover:border-glass-200/30'
              )}
            >
              <Bot className="w-3 h-3" />
              <span>Mr.Roboto / Jury</span>
              {phaseCounts.bot_pipeline > 0 && (
                <span className="bg-purple-500/30 text-purple-300 px-1.5 rounded-full text-[10px] tabular-nums">{phaseCounts.bot_pipeline}</span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setPhaseFilter('closed')}
              aria-pressed={phaseFilter === 'closed'}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors flex items-center gap-1.5',
                phaseFilter === 'closed'
                  ? 'bg-glass-200/20 border-glass-200/30 text-white'
                  : 'bg-glass-100/5 border-glass-200/10 text-glass-400 hover:text-white hover:border-glass-200/30'
              )}
            >
              <CheckSquare className="w-3 h-3" />
              <span>Closed</span>
              {phaseCounts.closed > 0 && (
                <span className="bg-glass-200/30 text-glass-300 px-1.5 rounded-full text-[10px] tabular-nums">{phaseCounts.closed}</span>
              )}
            </button>
          </div>
        </div>

        <AppealFilters
          filters={filters}
          onFiltersChange={setFilters}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />
      </div>

      {error && (
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load appeals"
          description={error}
          action={<button onClick={handleRefresh} className="glass-button">Retry</button>}
        />
      )}

      {isLoading && !error && <ListSkeleton />}

      {!isLoading && !error && filteredAppeals.length === 0 && (
        <EmptyState
          icon={Gavel}
          title="No appeals found"
          description={
            searchTerm ? 'No appeals match your search criteria.' :
            phaseFilter === 'staff_action' ? 'No appeals currently need staff action.' :
            phaseFilter === 'bot_pipeline' ? 'No appeals are currently in the Mr.Roboto / jury pipeline.' :
            phaseFilter === 'closed' ? 'No closed appeals found.' :
            filters.status ? 'No appeals with this status.' :
            activeTab === 'mine' ? 'You have no appeals assigned to you.' :
            'There are no appeals to display.'
          }
        />
      )}

      {!isLoading && !error && filteredAppeals.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-glass-400 tabular-nums">
            <span>
              Showing {filteredAppeals.length}{' '}
              {phaseFilter !== 'all' && `${phaseFilter === 'staff_action' ? 'staff-actionable' : phaseFilter === 'bot_pipeline' ? 'Mr.Roboto-managed' : 'closed'} `}
              appeals{totalAppeals > filteredAppeals.length ? ` of ${totalAppeals} total` : ''}
            </span>
          </div>
          <div className="space-y-3">
            {filteredAppeals.map(appeal => (
              <AppealCard key={appeal.id} appeal={appeal} />
            ))}
          </div>

          {totalPages > 1 && activeTab === 'all' && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setFilters(f => ({ ...f, page: Math.max(1, currentPage - 1) }))}
                className="glass-button-sm"
                disabled={currentPage <= 1}
              >
                Previous
              </button>
              <span className="text-xs text-glass-400 tabular-nums">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setFilters(f => ({ ...f, page: Math.min(totalPages, currentPage + 1) }))}
                className="glass-button-sm"
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
